/**
 * CustomersFilterSheet — the mobile Customers filter bottom sheet. Two facets,
 * both backed by real list fields: Governorate (multi-select chips derived from
 * the loaded customers) and Has orders (ordersCount > 0). Clone of
 * ProductsFilterSheet, extended to two facets.
 */

import { ModalSheet } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { CustomersFilterState } from "./customers-mobile-model";
import { activeFilterCount } from "./customers-mobile-model";

/** A selectable governorate option (id + already-localized label). */
export type GovernorateOption = { id: string; label: string };

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Distinct governorates present in the loaded page (incl. a "No governorate"
   *  option when some customers have none). */
  governorates: GovernorateOption[];
  value: CustomersFilterState;
  onChange: (next: CustomersFilterState) => void;
};

export function CustomersFilterSheet({
  isOpen,
  onClose,
  governorates,
  value,
  onChange,
}: Props) {
  const t = useTranslations();
  const total = activeFilterCount(value);

  const toggleGov = (id: string) => {
    const next = value.governorates.includes(id)
      ? value.governorates.filter((x) => x !== id)
      : [...value.governorates, id];
    onChange({ ...value, governorates: next });
  };

  return (
    <ModalSheet isOpen={isOpen} onClose={onClose} size="sm" surface="content">
      <div className="flex max-h-[80vh] flex-col">
        <div className="flex items-center gap-2.5 px-4 pb-2 pt-4">
          <span className="text-[color:var(--text-muted)]">
            <FilterIcon />
          </span>
          <h2 className="text-[15px] font-semibold tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.customers.mFilterTitle)}
          </h2>
          <span className="flex-1" />
          {total > 0 ? (
            <button
              type="button"
              onClick={() => onChange({ governorates: [], hasOrders: false })}
              className="text-[12.5px] font-semibold text-[color:var(--text-secondary)]"
            >
              {t(keys.orders.clearAll)}
            </button>
          ) : null}
        </div>

        <div className="overflow-y-auto px-4 pb-5 pt-1">
          {/* Has orders */}
          <div className="mb-4 flex flex-wrap gap-2">
            <Chip
              label={t(keys.customers.mFilterHasOrders)}
              on={value.hasOrders}
              onClick={() => onChange({ ...value, hasOrders: !value.hasOrders })}
            />
          </div>

          {/* Governorate */}
          <div className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
            {t(keys.customers.tableGovernorate)}
          </div>
          {governorates.length === 0 ? (
            <span className="text-[12.5px] text-[color:var(--text-muted)]">
              {t(keys.common.empty)}
            </span>
          ) : (
            <div className="flex flex-wrap gap-2">
              {governorates.map((g) => (
                <Chip
                  key={g.id}
                  label={g.label}
                  on={value.governorates.includes(g.id)}
                  onClick={() => toggleGov(g.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </ModalSheet>
  );
}

function Chip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full px-3 py-2 text-[13px] font-semibold transition-colors"
      style={{
        background: on ? "color-mix(in srgb, var(--accent-500) 14%, transparent)" : "transparent",
        border: `1px solid ${on ? "var(--accent-500)" : "var(--line)"}`,
        color: on ? "var(--accent-500)" : "var(--text-muted)",
      }}
    >
      {label}
    </button>
  );
}

function FilterIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      width={17}
      height={17}
      strokeWidth={1.9}
    >
      <path d="M3 5h18M6 12h12M10 19h4" />
    </svg>
  );
}
